import { create } from 'zustand';
import { giftCardsAPI } from '@matix/api-client';
import type { GiftCardConfig, GiftCardOrderResult } from '@matix/types';

/**
 * Gift-card state for the till.
 *
 * Two things live here:
 *   - `config`, loaded once on app boot like the tax and loyalty configs. It
 *     tells the UI whether to offer gift cards at all, which payment code means
 *     "paid with a gift card", and which catalog item a sale is rung on.
 *   - `issued`, the cards a just-charged order created. They are held here
 *     rather than inside the payment modal because that modal closes and resets
 *     the cart on success — and the card numbers and PINs it was handed are the
 *     only copy that will ever exist.
 *
 * Nothing here decides what a card is worth. Every balance shown to the cashier
 * comes from `POST /gift-cards/lookup`, and the server re-validates it under
 * lock when the order is charged.
 */

const DISABLED: GiftCardConfig = {
  enabled: false,
  schema_ready: false,
  payment_type_code: null,
  sale_item_id: null,
  denominations: [],
  allow_custom_value: true,
  min_value: 0,
  max_value: null,
  require_pin: true,
  pin_length: 4,
  default_expiry_months: null,
  allow_refund_to_card: true,
  max_cards_per_order: null,
};

interface GiftCardState {
  config: GiftCardConfig;
  configLoaded: boolean;
  /**
   * Cards created by the last charge, waiting to be shown to the cashier.
   * Cleared when the dialog is dismissed.
   */
  issued: GiftCardOrderResult['issued'];

  loadConfig: () => Promise<void>;
  setIssued: (cards: GiftCardOrderResult['issued']) => void;
  clearIssued: () => void;
  /** True when the till can take a gift card as payment. */
  canRedeem: () => boolean;
  /** True when the till can sell a gift card. */
  canSell: () => boolean;
}

export const useGiftCards = create<GiftCardState>((set, get) => ({
  config: DISABLED,
  configLoaded: false,
  issued: [],

  loadConfig: async () => {
    try {
      const config = await giftCardsAPI.config();
      set({ config, configLoaded: true });
    } catch {
      // A tenant without the gift-card schema (or an older API) simply has no
      // gift cards. Failing quietly keeps the till usable — a gift card is an
      // addition to the sale, never a precondition for making one.
      set({ config: DISABLED, configLoaded: true });
    }
  },

  setIssued: (issued) => set({ issued }),
  clearIssued: () => set({ issued: [] }),

  canRedeem: () => {
    const { config } = get();
    return config.enabled && config.payment_type_code != null;
  },

  canSell: () => {
    const { config } = get();
    return config.enabled && config.sale_item_id != null;
  },
}));
